import * as React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import ClearIcon from '@mui/icons-material/Clear';
import BadgeIcon from '@mui/icons-material/Badge';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';
import { getClients } from '../../api/clients';
import type { Client } from '../../api/types';
import ClientDetailsDialog from './components/ClientDetailsDialog';

const DEFAULT_PAGE_SIZE = 10;

interface SearchParams {
  fxboId: string;
  phone: string;
  email: string;
}

function normalizePhoneForSearch(raw: string): { value: string; error?: string } {
  const input = raw.trim();
  if (!input) return { value: '' };

  // Preserve leading `+` (if present) and strip formatting chars.
  const cleaned = input.startsWith('+')
    ? `+${input.slice(1).replace(/[^\d]/g, '')}`
    : input.replace(/[^\d]/g, '');

  // Full international format: allow for all countries.
  if (input.startsWith('+')) return { value: cleaned };

  // Kenya convenience formats:
  // - 07XXXXXXXX / 01XXXXXXXX  -> +2547XXXXXXXX / +2541XXXXXXXX
  // - 2547XXXXXXXX / 2541XXXXXXXX -> +2547XXXXXXXX / +2541XXXXXXXX
  if (/^0[71]\d{8}$/.test(cleaned)) return { value: `+254${cleaned.slice(1)}` };
  if (/^254[71]\d{8}$/.test(cleaned)) return { value: `+${cleaned}` };

  // All other countries must include the leading `+`.
  return {
    value: '',
    error: 'For non-Kenya numbers, use full international format starting with +.',
  };
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function ClearButton({ onClick }: { onClick: () => void }) {
  return (
    <IconButton
      aria-label="clear"
      onClick={onClick}
      edge="end"
      size="small"
      sx={{
        mr: 0.5,
        color: 'text.secondary',
        bgcolor: 'action.hover',
        borderRadius: '50%',
        width: 24,
        height: 24,
        '&:hover': { bgcolor: 'error.main', color: 'white' },
        transition: 'all 0.2s',
      }}
    >
      <ClearIcon sx={{ fontSize: 14 }} />
    </IconButton>
  );
}

export default function UserLookupPage() {
  const location = useLocation();
  const locationState = (location.state ?? {}) as { initialQuery?: string };

  const initialQuery = locationState.initialQuery?.trim() ?? '';
  const initialEmail = initialQuery.includes('@') ? initialQuery : '';
  const initialPhone = !initialQuery.includes('@') ? normalizePhoneForSearch(initialQuery).value : '';
  const initialFxbold = !initialQuery.includes('@') && !initialPhone ? initialQuery : '';

  // Draft inputs — what the user is currently typing
  const [fxboldInput, setFxboldInput] = React.useState(initialFxbold);
  const [phoneInput, setPhoneInput] = React.useState(initialPhone);
  const [emailInput, setEmailInput] = React.useState(initialEmail);
  const [phoneError, setPhoneError] = React.useState<string | undefined>(undefined);

  // Committed search — only updated when Search button is clicked
  const [committed, setCommitted] = React.useState<SearchParams>({
    fxboId: initialFxbold,
    phone: initialPhone,
    email: initialEmail,
  });
  const [searchTriggered, setSearchTriggered] = React.useState(false);

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [clients, setClients] = React.useState<Client[]>([]);
  const [totalElements, setTotalElements] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedClient, setSelectedClient] = React.useState<Client | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [fadingRowId, setFadingRowId] = React.useState<string | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const fadeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch on mount (all clients) and whenever committed search or pagination changes
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = {
      ...(committed.fxboId.trim() ? { fxboId: committed.fxboId.trim() } : {}),
      ...(committed.phone.trim() ? { phone: committed.phone.trim() } : {}),
      ...(committed.email.trim() ? { email: committed.email.trim() } : {}),
    };

    getClients({ page: page + 1, size: pageSize, ...params })
      .then((res) => {
        if (cancelled) return;
        if (!res.success || !res.data) {
          setError(res.message ?? 'Failed to load clients');
          setClients([]);
          setTotalElements(0);
          return;
        }
        setClients(res.data.clients ?? []);
        setTotalElements(res.data.totalElements ?? 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load clients');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [page, pageSize, committed, searchTriggered]);

  const handleSearch = React.useCallback(() => {
    setPage(0);

    const phoneNorm = normalizePhoneForSearch(phoneInput);
    const trimmedFxbo = fxboldInput.trim();
    const trimmedEmail = emailInput.trim();
    const hasOtherSearch = !!(trimmedFxbo || trimmedEmail);

    if (phoneNorm.error) {
      setPhoneError(phoneNorm.error);
      // If they only entered an invalid phone, don't trigger a broad "all clients" search.
      if (!hasOtherSearch) return;
    } else {
      setPhoneError(undefined);
    }

    setCommitted({
      fxboId: fxboldInput,
      phone: phoneNorm.value ? phoneNorm.value : phoneNorm.error ? '' : phoneInput,
      email: emailInput,
    });
    setSearchTriggered(true);
  }, [fxboldInput, phoneInput, emailInput]);

  const handleReset = React.useCallback(() => {
    setFxboldInput('');
    setPhoneInput('');
    setEmailInput('');
    setPhoneError(undefined);
    setCommitted({ fxboId: '', phone: '', email: '' });
    setSearchTriggered(false);
    setPage(0);
    setError(null);
  }, []);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  const openClientDetails = React.useCallback((client: Client) => {
    setSelectedClient(client);
    setDetailsOpen(true);
  }, []);

  React.useEffect(() => () => {
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
  }, []);

  const closeClientDetails = React.useCallback(() => {
    const selectedRowId = rowSelectionModel.ids.values().next().value as string | undefined;
    setDetailsOpen(false);
    setSelectedClient(null);
    if (!selectedRowId) return;

    setFadingRowId(selectedRowId);
    setRowSelectionModel({ type: 'include', ids: new Set() });

    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = setTimeout(() => {
      setFadingRowId(null);
      fadeTimeoutRef.current = null;
    }, 2000);
  }, [rowSelectionModel]);

  const handleClientUpdated = React.useCallback((updated: Client) => {
    setSelectedClient(updated);
    setClients((prev) =>
      prev.map((c) => {
        const id = c.fxBold ?? c.fxboId;
        const updatedId = updated.fxBold ?? updated.fxboId;
        return id && id === updatedId ? updated : c;
      }),
    );
  }, []);

  const handlePaginationModelChange = React.useCallback((model: GridPaginationModel) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  }, []);

  const hasActiveSearch = !!(committed.fxboId.trim() || committed.phone.trim() || committed.email.trim());

  const rows = React.useMemo(() =>
    clients.map((client, index) => {
      const fxboId = client.fxBold ?? client.fxboId ?? '—';
      return {
        id: fxboId !== '—' ? fxboId : `client-${index}`,
        fxBold: fxboId,
        firstName: client.firstName ?? '—',
        lastName: client.lastName ?? '—',
        country: client.country ?? '—',
        status: client.status ?? '—',
        email: client.contact?.primaryEmail ?? '—',
        primaryPhone: client.contact?.primaryPhone ?? '—',
        secondaryPhone: client.contact?.secondaryPhone ?? '—',
        _client: client,
      };
    }),
  [clients]);

  const columns: GridColDef[] = [
    { field: 'fxBold', headerName: 'Fxbo ID', flex: 1, minWidth: 100 },
    { field: 'firstName', headerName: 'First name', flex: 1, minWidth: 100 },
    { field: 'lastName', headerName: 'Last name', flex: 1, minWidth: 100 },
    { field: 'country', headerName: 'Country', flex: 0.8, minWidth: 90 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const status = params.value as string;
        const isActive = status === 'ACTIVE';
        return (
          <Chip
            label={formatStatus(status)}
            size="small"
            color={isActive ? 'success' : 'error'}
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
        );
      },
    },
    { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 180 },
    { field: 'primaryPhone', headerName: 'Primary phone', flex: 1, minWidth: 120 },
    { field: 'secondaryPhone', headerName: 'Secondary phone', flex: 1, minWidth: 120 },
  ];

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            fontWeight: 600,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 0.5,
          }}
        >
          Client Lookup
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search for clients by Fxbo ID, phone number, or email address
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(25, 118, 210, 0.02))'
              : 'linear-gradient(135deg, rgba(25, 118, 210, 0.02), rgba(25, 118, 210, 0.05))',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: (theme) => `0 4px 20px ${theme.palette.primary.main}15`,
          },
        }}
      >
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Fxbo ID"
              placeholder="Enter Fxbo ID"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  transition: 'all 0.2s',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputBase-input': { pl: 1 },
              }}
              value={fxboldInput}
              onChange={(e) => setFxboldInput(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon
                      sx={{
                        color: fxboldInput ? 'primary.main' : 'action.active',
                        transition: 'color 0.2s',
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: fxboldInput && (
                  <InputAdornment position="end">
                    <ClearButton onClick={() => setFxboldInput('')} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="07... or +254..."
              variant="outlined"
              helperText={phoneError}
              error={!!phoneError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  transition: 'all 0.2s',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: phoneError ? 'error.main' : 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputBase-input': { pl: 1 },
              }}
              value={phoneInput}
              onChange={(e) => {
                setPhoneInput(e.target.value);
                if (phoneError) setPhoneError(undefined);
              }}
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon
                      sx={{
                        color: phoneInput ? 'primary.main' : 'action.active',
                        transition: 'color 0.2s',
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: phoneInput && (
                  <InputAdornment position="end">
                    <ClearButton
                      onClick={() => {
                        setPhoneInput('');
                        if (phoneError) setPhoneError(undefined);
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Email"
              placeholder="user@example.com"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  transition: 'all 0.2s',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputBase-input': { pl: 1 },
              }}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon
                      sx={{
                        color: emailInput ? 'primary.main' : 'action.active',
                        transition: 'color 0.2s',
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: emailInput && (
                  <InputAdornment position="end">
                    <ClearButton onClick={() => setEmailInput('')} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 1.5, pt: 1 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  minWidth: 140,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}30`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: (theme) => `0 6px 20px ${theme.palette.primary.main}40`,
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                Search
              </Button>
              {searchTriggered && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleReset}
                  disabled={loading}
                  sx={{
                    minWidth: 100,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    borderWidth: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderWidth: 1.5,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Reset
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          minHeight: 400,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          transition: 'all 0.3s',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Results
            </Typography>
            {totalElements > 0 && (
              <Typography variant="body2" color="text.secondary">
                {totalElements.toLocaleString()} client{totalElements !== 1 ? 's' : ''} found
              </Typography>
            )}
          </Box>
          {hasActiveSearch && !loading && rows.length === 0 && (
            <Chip
              label="No results found"
              size="small"
              color="default"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>

        <DataGrid
          rows={rows}
          columns={columns}
          paginationMode="server"
          rowCount={totalElements}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection)}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableColumnResize
          density="comfortable"
          onCellClick={(params) =>
            setRowSelectionModel({
              type: 'include',
              ids: new Set([params.id]),
            })
          }
          onRowClick={(params) => openClientDetails((params.row as { _client: Client })._client)}
          getRowClassName={(params) =>
            params.id === fadingRowId ? 'user-lookup-row user-lookup-row-fading' : 'user-lookup-row'
          }
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderRadius: 1,
              mb: 0.5,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: '0.875rem',
            },
            '& .user-lookup-row': {
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
            '& .user-lookup-row-fading': {
              transition: 'background-color 2s ease',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-row': {
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': {
                  bgcolor: 'action.selected',
                },
              },
            },
          }}
        />
      </Paper>

      <ClientDetailsDialog
        open={detailsOpen}
        client={selectedClient}
        onClose={closeClientDetails}
        onClientUpdated={handleClientUpdated}
      />
    </>
  );
}
